import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion'
import Hero3DCoin from './Hero3DCoin'

/**
 * The opening scene of the archive — an asymmetric editorial composition
 * built around one real, verified artifact (see Hero3DCoin) rather than
 * an environment photo. Text anchored left, object toward the right, with
 * deliberate negative space between them so the headline reads as a
 * statement rather than a UI title. Scroll drives a slow content fade;
 * disabled entirely under prefers-reduced-motion.
 */
const HeroSection = () => {
  const sectionRef = useRef(null)
  const reduceMotion = useReducedMotion()

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  })

  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0])
  const contentY = useTransform(scrollYProgress, [0, 1], reduceMotion ? [0, 0] : [0, 40])

  return (
    <section ref={sectionRef} className="relative w-full min-h-[92vh] md:min-h-[100vh] overflow-hidden bg-museum-black">
      {/* Faint archival grain, never distracting */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-museum-black via-museum-black to-museum-charcoal pointer-events-none" />

      <motion.div
        className="relative z-10 min-h-[92vh] md:min-h-[100vh] flex items-center px-6 md:px-10 pt-24 pb-20"
        style={{ opacity: contentOpacity, y: contentY }}
      >
        <div className="max-w-[1320px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center">
          {/* Text — anchored left, occupies the dominant share of hierarchy */}
          <motion.div
            className="lg:col-span-7 text-center lg:text-left"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
          >
            <p className="text-[10px] uppercase tracking-[0.4em] text-museum-antique-gold font-sans font-medium mb-9">
              Heritage &middot; Digital Archive &amp; Museum
            </p>

            <h1
              className="font-editorial font-normal italic text-museum-ivory leading-[0.88] mb-10"
              style={{ fontSize: 'clamp(3rem, 7.4vw, 6.75rem)', letterSpacing: '-0.01em' }}
            >
              Every object
              <br />
              has a story.
            </h1>

            <p className="text-museum-ivory/55 font-sans text-base md:text-lg max-w-md mx-auto lg:mx-0 mb-12 leading-relaxed">
              A digital archive for collectors, researchers, and those who
              believe history deserves to be remembered — catalogued with
              the rigor a museum would apply to its own collection.
            </p>

            <div className="flex items-center justify-center lg:justify-start">
              <Link to="/signup">
                <button className="px-9 py-4 border border-museum-antique-gold/70 text-museum-ivory font-sans text-[11px] uppercase tracking-[0.24em] font-medium hover:bg-museum-antique-gold hover:text-museum-black hover:border-museum-antique-gold transition-colors duration-500">
                  Explore the Archive
                </button>
              </Link>
            </div>
          </motion.div>

          {/* The artifact — offset right, substantial negative space around it */}
          <motion.div
            className="lg:col-span-5 lg:col-start-9"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <Hero3DCoin />
          </motion.div>
        </div>
      </motion.div>

      {/* Wayfinding scroll cue */}
      <motion.div
        className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-3 z-10"
        style={{ opacity: contentOpacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.3 }}
      >
        <span className="w-px h-10 bg-museum-antique-gold/30" />
        <span className="text-[9px] font-sans tracking-[0.3em] uppercase text-museum-ivory/35">Scroll</span>
      </motion.div>
    </section>
  )
}

export default HeroSection
